import {Placement} from '@popperjs/core';
import React, {FC, PropsWithChildren, ReactNode, useState} from 'react';
import styled from 'styled-components';

import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {TooltipCoordinator} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {LearnMoreButton} from '../../../../components/common/callToAction/learnMoreButton';
import {EducationTooltipCoordinator} from '../../../../components/common/tooltips/educationTooltip/educationTooltipCoordinator';

/*
 * Types.
 */

export interface AnalyticsEducationTooltip {
  title: ReactNode;
  tooltipContent: ReactNode;
  learnMoreUrl: string;
}

export type AnalyticsHelpTooltip = AnalyticsEducationTooltip | ReactNode;

/*
 * Props.
 */

interface AnalyticsTooltipsProps {
  tooltip: AnalyticsHelpTooltip | undefined;
  placement?: Placement;
  className?: string;
  delay?: number;
}

/*
 * Component.
 */

export const AnalyticsTooltip: FC<PropsWithChildren<AnalyticsTooltipsProps>> = ({
  tooltip: complexTooltip,
  className,
  placement = 'top',
  delay,
  children,
}) => {
  if (isComplexAnalyticsTooltip(complexTooltip)) {
    return (
      <EducationTooltipCoordinator
        title={complexTooltip.title}
        tooltip={complexTooltip.tooltipContent}
        placement={placement}
        className={className}
        delay={delay}
        buttons={[
          <LearnMoreButton
            size={VisualSizesEnum.SMALL}
            variant={ButtonVariantsEnum.PRIMARY}
            helpPageUrl={complexTooltip.learnMoreUrl}
          />,
        ]}
      >
        {children || <TooltipIconWithHover />}
      </EducationTooltipCoordinator>
    );
  }

  return (
    <TooltipCoordinator
      isMultiline
      className={className}
      placement={placement}
      render={() => complexTooltip && <TooltipContentWrapper>{complexTooltip}</TooltipContentWrapper>}
    >
      {children || <TooltipIconWithHover />}
    </TooltipCoordinator>
  );
};

/*
 * Helpers.
 */

export function isComplexAnalyticsTooltip(
  tooltip: AnalyticsHelpTooltip,
): tooltip is AnalyticsEducationTooltip {
  return Boolean(tooltip && typeof tooltip === 'object' && 'tooltipContent' in tooltip);
}

interface TooltipIconWithHoverProps {
  color?: string;
}

export const TooltipIconWithHover: FC<PropsWithChildren<TooltipIconWithHoverProps>> = (props) => {
  const theme = useUserTheme();
  const {color} = props;

  const [isHovered, setHover] = useState(false);

  return (
    <div onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
      <Icon
        name={isHovered ? 'infoFilledSmall' : 'infoSmall'}
        color={color || theme.greys.shade70}
        hoverColor={theme.greys.shade80}
      />
    </div>
  );
};

/*
 * Styles.
 */

const TooltipContentWrapper = styled.section`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  size: ${fontSizes.verySmall};

  // For design
  line-height: 18px;
`;
